import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Textarea } from '@/components/ui/textarea'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs'
import {
  AlertCircle,
  Loader2,
  Bug,
  CheckCircle,
  XCircle,
  Eye,
  Filter,
  TrendingUp,
  AlertTriangle,
  Info,
} from 'lucide-react'
import { supabase } from '@/lib/supabase'
import { AdminUser } from '@/lib/authService'

interface PlatformError {
  id: string
  church_id: string | null
  user_id: string | null
  error_type: string
  error_code: string | null
  error_message: string
  stack_trace: string | null
  url: string | null
  method: string | null
  user_agent: string | null
  browser: string | null
  os: string | null
  device: string | null
  severity: 'low' | 'medium' | 'high' | 'critical'
  resolved: boolean
  resolved_at: string | null
  resolved_by: string | null
  resolution_notes: string | null
  created_at: string
  church_name?: string
  user_email?: string
}

interface ErrorSummary {
  error_type: string
  error_code: string | null
  severity: string
  error_count: number
  affected_churches: number
  affected_users: number
  last_occurred: string
}

interface ErrorTrackingProps {
  user: AdminUser | null
}

export default function ErrorTracking({ user }: ErrorTrackingProps) {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [errors, setErrors] = useState<PlatformError[]>([])
  const [errorSummary, setErrorSummary] = useState<ErrorSummary[]>([])
  const [filteredErrors, setFilteredErrors] = useState<PlatformError[]>([])
  const [selectedError, setSelectedError] = useState<PlatformError | null>(null)
  const [showDetailsDialog, setShowDetailsDialog] = useState(false)
  const [showResolveDialog, setShowResolveDialog] = useState(false)
  const [resolutionNotes, setResolutionNotes] = useState('')
  const [resolving, setResolving] = useState(false)

  // Filters
  const [activeTab, setActiveTab] = useState('unresolved')
  const [searchTerm, setSearchTerm] = useState('')
  const [severityFilter, setSeverityFilter] = useState<string>('all')
  const [errorTypeFilter, setErrorTypeFilter] = useState<string>('all')

  // Stats
  const [stats, setStats] = useState({
    total: 0,
    unresolved: 0,
    critical: 0,
    high: 0,
    today: 0,
  })

  // Check if user is super admin
  if (user?.adminLevel !== 'super_admin') {
    return (
      <div className="flex items-center justify-center h-screen">
        <Card className="w-96">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-600">
              <AlertCircle className="h-5 w-5" />
              Access Denied
            </CardTitle>
            <CardDescription>
              You do not have permission to access error tracking.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => navigate('/')} variant="outline" className="w-full">
              Return to Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  useEffect(() => {
    loadErrors()
    loadErrorSummary()
  }, [])

  useEffect(() => {
    applyFilters()
  }, [errors, activeTab, searchTerm, severityFilter, errorTypeFilter])

  async function loadErrors() {
    try {
      setLoading(true)

      const { data, error } = await supabase
        .from('platform_errors')
        .select(`
          *,
          churches:church_id(name),
          profiles:user_id(email)
        `)
        .order('created_at', { ascending: false })
        .limit(500)

      if (error) throw error

      const enrichedErrors = (data || []).map((err: any) => ({
        ...err,
        church_name: err.churches?.name,
        user_email: err.profiles?.email,
      }))

      setErrors(enrichedErrors)
      calculateStats(enrichedErrors)
    } catch (error) {
      console.error('Error loading errors:', error)
    } finally {
      setLoading(false)
    }
  }

  async function loadErrorSummary() {
    try {
      const { data, error } = await supabase
        .from('error_summary')
        .select('*')
        .limit(20)

      if (error) throw error

      setErrorSummary(data || [])
    } catch (error) {
      console.error('Error loading error summary:', error)
    }
  }

  function calculateStats(errorData: PlatformError[]) {
    const today = new Date()
    today.setHours(0, 0, 0, 0)

    const todayErrors = errorData.filter(err => new Date(err.created_at) >= today)
    const unresolvedErrors = errorData.filter(err => !err.resolved)
    const criticalErrors = unresolvedErrors.filter(err => err.severity === 'critical')
    const highErrors = unresolvedErrors.filter(err => err.severity === 'high')

    setStats({
      total: errorData.length,
      unresolved: unresolvedErrors.length,
      critical: criticalErrors.length,
      high: highErrors.length,
      today: todayErrors.length,
    })
  }

  function applyFilters() {
    let filtered = [...errors]

    // Tab filter
    if (activeTab === 'unresolved') {
      filtered = filtered.filter(err => !err.resolved)
    } else if (activeTab === 'resolved') {
      filtered = filtered.filter(err => err.resolved)
    }

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(err =>
        err.error_message?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        err.error_type?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        err.church_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        err.user_email?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    }

    // Severity filter
    if (severityFilter !== 'all') {
      filtered = filtered.filter(err => err.severity === severityFilter)
    }

    // Error type filter
    if (errorTypeFilter !== 'all') {
      filtered = filtered.filter(err => err.error_type === errorTypeFilter)
    }

    setFilteredErrors(filtered)
  }

  function getSeverityBadge(severity: string) {
    switch (severity) {
      case 'critical':
        return <Badge className="bg-red-500"><AlertCircle className="h-3 w-3 mr-1" />Critical</Badge>
      case 'high':
        return <Badge className="bg-orange-500"><AlertTriangle className="h-3 w-3 mr-1" />High</Badge>
      case 'medium':
        return <Badge className="bg-yellow-500"><Info className="h-3 w-3 mr-1" />Medium</Badge>
      default:
        return <Badge variant="outline"><Info className="h-3 w-3 mr-1" />Low</Badge>
    }
  }

  function handleViewDetails(error: PlatformError) {
    setSelectedError(error)
    setShowDetailsDialog(true)
  }

  function handleResolve(error: PlatformError) {
    setSelectedError(error)
    setResolutionNotes('')
    setShowResolveDialog(true)
  }

  async function handleMarkResolved() {
    if (!selectedError || !user) return

    try {
      setResolving(true)

      const { error } = await supabase
        .from('platform_errors')
        .update({
          resolved: true,
          resolved_at: new Date().toISOString(),
          resolved_by: user.id,
          resolution_notes: resolutionNotes || null,
        })
        .eq('id', selectedError.id)

      if (error) throw error

      // Log audit event
      await supabase.rpc('log_audit_event', {
        p_admin_user_id: user.id,
        p_admin_email: user.email,
        p_admin_name: `${user.firstName} ${user.lastName}`,
        p_action_type: 'error_resolved',
        p_action_category: 'system',
        p_target_church_id: selectedError.church_id,
        p_target_church_name: selectedError.church_name || null,
        p_action_details: {
          error_id: selectedError.id,
          error_type: selectedError.error_type,
          error_message: selectedError.error_message,
          resolution_notes: resolutionNotes,
        },
        p_severity: 'info'
      })

      setShowResolveDialog(false)
      setSelectedError(null)
      setResolutionNotes('')
      await loadErrors()
      await loadErrorSummary()
    } catch (error) {
      console.error('Error marking as resolved:', error)
      alert('Failed to mark error as resolved')
    } finally {
      setResolving(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-primary" />
          <p className="text-muted-foreground">Loading error tracking data...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <Bug className="h-8 w-8" />
            Error Tracking
          </h1>
          <p className="text-muted-foreground mt-1">
            Monitor and resolve platform errors
          </p>
        </div>
        <Button onClick={() => loadErrors()} variant="outline">
          <TrendingUp className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-5">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Total Errors
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.total}</div>
            <p className="text-xs text-muted-foreground">Last 500 errors</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Unresolved
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-500">{stats.unresolved}</div>
            <p className="text-xs text-muted-foreground">Need attention</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Critical
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-500">{stats.critical}</div>
            <p className="text-xs text-muted-foreground">Urgent fixes</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              High Priority
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-500">{stats.high}</div>
            <p className="text-xs text-muted-foreground">Important</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Today
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.today}</div>
            <p className="text-xs text-muted-foreground">Errors today</p>
          </CardContent>
        </Card>
      </div>

      {/* Error Summary */}
      {errorSummary.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Top Errors</CardTitle>
            <CardDescription>Most frequent unresolved errors</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {errorSummary.slice(0, 5).map((summary, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-3 rounded-lg bg-muted/50"
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{summary.error_type}</span>
                      {summary.error_code && (
                        <Badge variant="outline" className="text-xs">
                          {summary.error_code}
                        </Badge>
                      )}
                      {getSeverityBadge(summary.severity)}
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">
                      {summary.error_count} occurrences •
                      {summary.affected_churches} churches •
                      {summary.affected_users} users
                    </p>
                  </div>
                  <div className="text-2xl font-bold text-red-500">
                    {summary.error_count}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters and Tabs */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Filter className="h-5 w-5" />
              Filters
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="unresolved">
                  Unresolved ({stats.unresolved})
                </TabsTrigger>
                <TabsTrigger value="resolved">Resolved</TabsTrigger>
                <TabsTrigger value="all">All ({stats.total})</TabsTrigger>
              </TabsList>
            </Tabs>

            <div className="grid gap-4 md:grid-cols-4">
              <Input
                placeholder="Search errors..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />

              <Select value={severityFilter} onValueChange={setSeverityFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="Severity" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Severities</SelectItem>
                  <SelectItem value="critical">Critical</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="low">Low</SelectItem>
                </SelectContent>
              </Select>

              <Select value={errorTypeFilter} onValueChange={setErrorTypeFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="Error Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="client_error">Client Error</SelectItem>
                  <SelectItem value="server_error">Server Error</SelectItem>
                  <SelectItem value="database_error">Database Error</SelectItem>
                  <SelectItem value="edge_function_error">Edge Function</SelectItem>
                </SelectContent>
              </Select>

              {(searchTerm || severityFilter !== 'all' || errorTypeFilter !== 'all') && (
                <Button
                  variant="ghost"
                  onClick={() => {
                    setSearchTerm('')
                    setSeverityFilter('all')
                    setErrorTypeFilter('all')
                  }}
                >
                  Clear Filters
                </Button>
              )}
            </div>

            {filteredErrors.length !== errors.length && (
              <p className="text-sm text-muted-foreground">
                Showing {filteredErrors.length} of {errors.length} errors
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Errors Table */}
      <Card>
        <CardHeader>
          <CardTitle>Error Log</CardTitle>
          <CardDescription>
            {filteredErrors.length} errors matching your filters
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date & Time</TableHead>
                  <TableHead>Error Type</TableHead>
                  <TableHead>Message</TableHead>
                  <TableHead>Church</TableHead>
                  <TableHead>Severity</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredErrors.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                      No errors found matching your filters.
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredErrors.map((error) => (
                    <TableRow key={error.id}>
                      <TableCell className="font-mono text-sm">
                        {new Date(error.created_at).toLocaleString()}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{error.error_type}</Badge>
                        {error.error_code && (
                          <div className="text-xs text-muted-foreground mt-1">
                            {error.error_code}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="max-w-md truncate">
                        {error.error_message}
                      </TableCell>
                      <TableCell>
                        {error.church_name || <span className="text-muted-foreground">N/A</span>}
                      </TableCell>
                      <TableCell>
                        {getSeverityBadge(error.severity)}
                      </TableCell>
                      <TableCell>
                        {error.resolved ? (
                          <Badge className="bg-green-500">
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Resolved
                          </Badge>
                        ) : (
                          <Badge className="bg-orange-500">
                            <XCircle className="h-3 w-3 mr-1" />
                            Open
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleViewDetails(error)}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          {!error.resolved && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleResolve(error)}
                            >
                              <CheckCircle className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Error Details Dialog */}
      <Dialog open={showDetailsDialog} onOpenChange={setShowDetailsDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-white dark:bg-gray-900">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bug className="h-5 w-5" />
              Error Details
            </DialogTitle>
            <DialogDescription>
              Complete error information and stack trace
            </DialogDescription>
          </DialogHeader>

          {selectedError && (
            <div className="space-y-4">
              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Timestamp</p>
                  <p className="font-mono">{new Date(selectedError.created_at).toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Severity</p>
                  <div className="mt-1">{getSeverityBadge(selectedError.severity)}</div>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Error Type</p>
                  <Badge variant="outline">{selectedError.error_type}</Badge>
                </div>
                {selectedError.error_code && (
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Error Code</p>
                    <p className="font-mono">{selectedError.error_code}</p>
                  </div>
                )}
                {selectedError.church_name && (
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Church</p>
                    <p>{selectedError.church_name}</p>
                  </div>
                )}
                {selectedError.user_email && (
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">User</p>
                    <p>{selectedError.user_email}</p>
                  </div>
                )}
              </div>

              {/* Error Message */}
              <div>
                <p className="text-sm font-medium text-muted-foreground mb-2">Error Message</p>
                <div className="bg-muted p-3 rounded-md">
                  <p className="text-sm font-mono">{selectedError.error_message}</p>
                </div>
              </div>

              {/* URL and Method */}
              {(selectedError.url || selectedError.method) && (
                <div className="grid grid-cols-2 gap-4">
                  {selectedError.url && (
                    <div>
                      <p className="text-sm font-medium text-muted-foreground mb-2">URL</p>
                      <p className="text-sm font-mono break-all">{selectedError.url}</p>
                    </div>
                  )}
                  {selectedError.method && (
                    <div>
                      <p className="text-sm font-medium text-muted-foreground mb-2">Method</p>
                      <Badge variant="outline">{selectedError.method}</Badge>
                    </div>
                  )}
                </div>
              )}

              {/* Stack Trace */}
              {selectedError.stack_trace && (
                <div>
                  <p className="text-sm font-medium text-muted-foreground mb-2">Stack Trace</p>
                  <pre className="bg-muted p-3 rounded-md text-xs overflow-x-auto max-h-60">
                    {selectedError.stack_trace}
                  </pre>
                </div>
              )}

              {/* Browser/Device Info */}
              {(selectedError.browser || selectedError.os || selectedError.device) && (
                <div className="grid grid-cols-3 gap-4">
                  {selectedError.browser && (
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Browser</p>
                      <p className="text-sm">{selectedError.browser}</p>
                    </div>
                  )}
                  {selectedError.os && (
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">OS</p>
                      <p className="text-sm">{selectedError.os}</p>
                    </div>
                  )}
                  {selectedError.device && (
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Device</p>
                      <p className="text-sm">{selectedError.device}</p>
                    </div>
                  )}
                </div>
              )}

              {/* Resolution Info */}
              {selectedError.resolved && (
                <div className="bg-green-50 dark:bg-green-950 p-4 rounded-lg">
                  <p className="text-sm font-medium text-green-600 dark:text-green-400 mb-2">
                    ✅ Resolved
                  </p>
                  {selectedError.resolved_at && (
                    <p className="text-sm text-muted-foreground">
                      Resolved on: {new Date(selectedError.resolved_at).toLocaleString()}
                    </p>
                  )}
                  {selectedError.resolution_notes && (
                    <div className="mt-2">
                      <p className="text-sm font-medium">Resolution Notes:</p>
                      <p className="text-sm mt-1">{selectedError.resolution_notes}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Resolve Dialog */}
      <Dialog open={showResolveDialog} onOpenChange={setShowResolveDialog}>
        <DialogContent className="bg-white dark:bg-gray-900">
          <DialogHeader>
            <DialogTitle>Mark Error as Resolved</DialogTitle>
            <DialogDescription>
              Add resolution notes to explain how this error was fixed.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <p className="text-sm font-medium mb-2">Error Message</p>
              <p className="text-sm text-muted-foreground bg-muted p-2 rounded">
                {selectedError?.error_message}
              </p>
            </div>

            <div>
              <label className="text-sm font-medium">Resolution Notes (Optional)</label>
              <Textarea
                placeholder="Describe how this error was fixed or why it can be ignored..."
                value={resolutionNotes}
                onChange={(e) => setResolutionNotes(e.target.value)}
                rows={4}
                className="mt-2"
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowResolveDialog(false)}
              disabled={resolving}
            >
              Cancel
            </Button>
            <Button
              onClick={handleMarkResolved}
              disabled={resolving}
            >
              {resolving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Resolving...
                </>
              ) : (
                <>
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Mark Resolved
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
